/**
 * optional-products.js
 *
 * Adds main product + selected optional stand-alone products to cart in one request,
 * then triggers the theme's cart drawer update + open flow (ThemeEvents.cartUpdate => 'cart:update').
 *
 * Requirements:
 * - Optional checkboxes must have class ".js-optional" and value="{{ variant.id }}"
 * - Works with multiple ATC forms (main + sticky)
 */

(() => {
  let busy = false;

  const SELECTOR_FORMS = 'form[action*="/cart/add"]';
  const SELECTOR_OPTIONAL_CHECKED = '.js-optional:checked';

  // ---------------------------
  // Helpers
  // ---------------------------
  function getCheckedOptionalVariantIds() {
    return Array.from(document.querySelectorAll(SELECTOR_OPTIONAL_CHECKED))
      .map((el) => Number(el.value))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  function getMainVariantIdFromForm(form) {
    // Most reliable: theme keeps this input updated on variant change
    const idInput = form.querySelector('input[name="id"]');
    const id = Number(idInput?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getQtyFromForm(form) {
    const qtyInput = form.querySelector('input[name="quantity"]');
    const q = Number(qtyInput?.value || 1);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function getDrawerSectionIds() {
    // Prefer reading from the drawer component if your theme exposes it
    const drawer = document.querySelector('cart-drawer-component');

    // Common patterns: sections attr / data-sections on the component
    const raw =
      drawer?.getAttribute('sections') ||
      drawer?.dataset?.sections ||
      '';

    if (raw) {
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    // Fallbacks (Dawn-like defaults). Adjust if needed.
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  async function cartAdd(items) {
    const sections = getDrawerSectionIds();

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    // Shopify returns JSON on both success + error (but keep it defensive)
    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      console.error('cart/add.js failed', res.status, json);
      throw new Error(`cart_add_failed_${res.status}`);
    }

    return json; // typically contains: items, sections (html), item_count, etc.
  }

  function dispatchCartUpdate(cartResponse, sourceId = 'optional-products') {
    // Your theme uses ThemeEvents.cartUpdate => 'cart:update'
    const ev = new Event('cart:update', { bubbles: true });

    // Match the theme's CartAddEvent-ish detail shape
    ev.detail = {
      resource: cartResponse,
      sourceId,
      data: {
        source: 'optional-products',
        didError: false,
        itemCount: cartResponse?.item_count,
        sections: cartResponse?.sections, // critical for drawer HTML refresh
      },
    };

    document.dispatchEvent(ev);
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    // Fallback: trigger a generic open event if the theme listens
    document.dispatchEvent(new Event('cart:open', { bubbles: true }));
  }

  // ---------------------------
  // Core handler
  // ---------------------------
  async function handleOptionalAdd(form, submitBtn) {
    const optionalIds = getCheckedOptionalVariantIds();
    if (!optionalIds.length) return false; // no optionals => let theme handle default ATC

    const mainVariantId = getMainVariantIdFromForm(form);
    if (!mainVariantId) return false;

    if (busy) return true; // prevent double submit (sticky + main)
    busy = true;

    if (submitBtn) submitBtn.disabled = true;

    try {
      const items = [
        { id: mainVariantId, quantity: getQtyFromForm(form) },
        ...optionalIds.map((id) => ({ id, quantity: 1 })),
      ];

      const cartResponse = await cartAdd(items);

      // Tell the theme to re-render cart sections + allow auto-open logic
      dispatchCartUpdate(cartResponse);

      // Ensure drawer opens (even if auto-open is not present)
      openCartDrawer();

      return true;
    } catch (err) {
      console.error('Optional add failed:', err);
      alert('Errore durante l’aggiunta degli optional. Riprova.');
      return true; // handled
    } finally {
      busy = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ---------------------------
  // Bindings (main + sticky)
  // ---------------------------
  function bindForm(form) {
    if (form.dataset.optionalsBound === '1') return;
    form.dataset.optionalsBound = '1';

    // Intercept submit
    form.addEventListener('submit', async (e) => {
      const submitBtn = form.querySelector('[type="submit"]');
      const handled = await handleOptionalAdd(form, submitBtn);
      if (handled) e.preventDefault();
    });

    // Intercept button clicks too (some themes do weird submit flows)
    form.querySelectorAll('[type="submit"], button[name="add"]').forEach((btn) => {
      btn.addEventListener(
        'click',
        async (e) => {
          const handled = await handleOptionalAdd(form, btn);
          if (handled) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  function init() {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);

    // Re-bind on section load (theme editor / dynamic re-render)
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    });

    // If your theme emits these on variant changes, rebinding is safe
    document.addEventListener('variant:update', () => {
      document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    });
  }

  document.addEventListener('DOMContentLoaded', init);
})();

// ---------------------------------
// Optional description "read more"
// Localized via data-more/data-less
// ---------------------------------
document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('.product-optionals__desc-toggle');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const item = btn.closest('.product-optionals__item');
  if (!item) return;

  const expanded = item.classList.toggle('is-desc-expanded');
  btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

  const more = btn.dataset.more || 'Read more';
  const less = btn.dataset.less || 'Read less';
  btn.textContent = expanded ? less : more;
}, true);

// ---------------------------------
// Optional products "view all / view less"
// Markup expected:
// - wrapper: .product-optionals
// - toggle:  .js-optionals-toggle (recommended)
// - extra items: [data-optional-extra="1"] (initially hidden in Liquid)
// - wrapper has data-optionals-limit="4" (optional)
// ---------------------------------
(() => {
  const WRAP_SEL = '.product-optionals';
  const TOGGLE_SEL = '.js-optionals-toggle, .product-optionals__toggle';
  const EXTRA_SEL = '[data-optional-extra="1"]';

  let lastTouchTs = 0;

  function setOpen(wrap, open) {
    wrap.classList.toggle('is-open', open);

    const toggle = wrap.querySelector(TOGGLE_SEL);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    wrap.querySelectorAll(EXTRA_SEL).forEach((el) => {
      el.hidden = !open;
    });
  }

  function init(root = document) {
    root.querySelectorAll(WRAP_SEL).forEach((wrap) => {
      if (wrap.dataset.optionalsToggleBound === '1') return;
      wrap.dataset.optionalsToggleBound = '1';

      const toggle = wrap.querySelector(TOGGLE_SEL);
      if (!toggle) return;

      const hasExtras = !!wrap.querySelector(EXTRA_SEL);
      if (!hasExtras) {
        toggle.style.display = 'none';
        return;
      }

      // Ensure initial state is collapsed (extras are hidden in Liquid, but reinforce)
      setOpen(wrap, false);

      // Make the toggle keyboard-accessible if it's not naturally focusable
      if (!toggle.matches('a,button,input,summary') && !toggle.hasAttribute('tabindex')) {
        toggle.setAttribute('tabindex', '0');
        toggle.setAttribute('role', toggle.getAttribute('role') || 'button');
      }
    });
  }

  function handleToggleEvent(e) {
    const toggle = e.target.closest?.(TOGGLE_SEL);
    if (!toggle) return;

    // Only handle toggles inside a product-optionals block
    const wrap = toggle.closest?.(WRAP_SEL);
    if (!wrap) return;

    // Dedupe: iOS often fires touchend + click
    const now = Date.now();
    if (e.type === 'click' && now - lastTouchTs < 650) {
      e.preventDefault();
      return;
    }
    if (e.type === 'touchend') lastTouchTs = now;

    e.preventDefault();
    e.stopPropagation();

    const open = wrap.classList.contains('is-open');
    setOpen(wrap, !open);

    // If toggle contains "more/less" spans (as in your Liquid), swap via CSS class only.
    // No text mutations here by default to keep it theme-safe.
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  // Capture makes it harder for theme scripts to swallow the event first
  document.addEventListener('touchend', handleToggleEvent, true);
  document.addEventListener('click', handleToggleEvent, true);

  document.addEventListener('keydown', (e) => {
    const toggle = e.target.closest?.(TOGGLE_SEL);
    if (!toggle) return;
    const wrap = toggle.closest?.(WRAP_SEL);
    if (!wrap) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Trigger click on the actual toggle element
      toggle.click();
    }
  }, true);
})();
